import { useEffect, useState } from 'react';
import { LogOut, AlertTriangle, PieChart as PieIcon, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { getIncome } from '@/lib/api';
import { fmtMoney } from './dashboardUtils';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ── Income Card with sparkline ──────────────────────────────── */
function IncomeChartCard({ data, onShowIncome }) {
  const [trend, setTrend] = useState([]);
  const incomeChange = data.prev_income > 0 ? ((data.current_income - data.prev_income) / data.prev_income * 100) : 0;

  useEffect(() => {
    const year = new Date().getFullYear();
    const month = new Date().getMonth() + 1;
    getIncome(year).then(d => {
      const series = (d.months || [])
        .filter(m => m.month <= month)
        .map(m => ({ label: `${MONTH_NAMES[m.month - 1]} ${year}`, value: m.total || 0 }));
      setTrend(series);
    }).catch(() => setTrend([]));
  }, []);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow flex flex-col" onClick={onShowIncome} data-testid="metric-card-income">
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-sm text-gray-500">This Month's Income</p>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="text-xs text-gray-500 bg-gray-50 hover:bg-gray-100 rounded-md px-2.5 py-1 border border-gray-200"
        >
          This Month
        </button>
      </div>
      <p className="text-[40px] font-bold tabular-nums text-gray-900 leading-none mb-2" data-testid="metric-income">{fmtMoney(data.current_income)}</p>
      <div className="flex items-center gap-1.5">
        {incomeChange >= 0
          ? <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" />
          : <ArrowDownRight className="h-3.5 w-3.5 text-red-500" />}
        <span className={`text-xs font-medium ${incomeChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {incomeChange >= 0 ? '+' : ''}{incomeChange.toFixed(1)}% vs last month
        </span>
      </div>
      <div className="mt-4 -mx-2 flex-1 min-h-[100px]">
        {trend.length > 1 && (
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={trend}>
              <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#10b981' }} />
              <Tooltip
                cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }}
                contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                formatter={(value) => [fmtMoney(value), '']}
                labelFormatter={(_, p) => p && p[0] ? p[0].payload.label : ''}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

/* ── Compact stat card (right of income) ──────────────────── */
function StatCard({ icon: Icon, iconBg, iconColor, label, sublabel, value, footer, footerColor = 'text-gray-400', onClick, testId }) {
  return (
    <div
      className={`bg-white rounded-2xl p-6 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center mb-3`}>
        <Icon className={`h-4.5 w-4.5 ${iconColor}`} />
      </div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-xs text-gray-400 mb-2">{sublabel}</p>
      <p className="text-[36px] font-bold tabular-nums text-gray-900 leading-none">{value}</p>
      {footer && <p className={`text-xs mt-2 ${footerColor}`}>{footer}</p>}
    </div>
  );
}

/* ── Top metrics row ──────────────────────────────────────── */
export function DashboardTopMetrics({ data, onShowIncome, onShowMoveOuts, onShowVacancies, onShowVacancyRate }) {
  const vacancyChange = data.vacancy_rate - data.prev_vacancy_rate;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2"><IncomeChartCard data={data} onShowIncome={onShowIncome} /></div>
      <StatCard
        icon={LogOut} iconBg="bg-emerald-50" iconColor="text-emerald-600"
        label="Move-Outs" sublabel="This Week"
        value={data.move_outs_count}
        footer={data.move_outs_count > 0 ? `↑ ${data.move_outs_count} from last week` : 'None this week'}
        footerColor="text-emerald-600"
        onClick={onShowMoveOuts}
        testId="metric-card-moveouts"
      />
      <StatCard
        icon={AlertTriangle} iconBg="bg-amber-50" iconColor="text-amber-600"
        label="Vacancies" sublabel="Upcoming"
        value={data.vacancy_count}
        footer="Next 60 days"
        onClick={onShowVacancies}
        testId="metric-card-vacancies"
      />
      <StatCard
        icon={PieIcon} iconBg="bg-violet-50" iconColor="text-violet-600"
        label="Vacancy Rate" sublabel="Overall"
        value={`${data.vacancy_rate}%`}
        footer={`${vacancyChange <= 0 ? '↓' : '↑'} ${Math.abs(vacancyChange).toFixed(1)}pp vs last month`}
        footerColor={vacancyChange <= 0 ? 'text-emerald-600' : 'text-red-500'}
        onClick={onShowVacancyRate}
        testId="metric-card-vacancy-rate"
      />
    </div>
  );
}
